import InsightLayout from "../../components/InsightLayout";
import FoerderHinweis from "../../components/FoerderHinweis";

const SUBTITLE = `Die meisten „Prompt-Engineering-Kurse" lehren Tricks, die in sechs Monaten veraltet sind — „sag dem Modell, es sei ein Experte", „biete ihm Trinkgeld an", „droh ihm". Das ist Aberglaube, kein Engineering. Was bleibt, sind eine Handvoll Patterns, die seit drei Jahren funktionieren und es weiter tun werden. Hier sind die fünf, die ich in zwölf Mandaten immer wieder eingesetzt habe — mit konkreten Vorher-Nachher-Beispielen aus der Praxis.`;

const PromptEngineeringMittelstand = () => {
  return (
    <InsightLayout
      title="Prompt-Engineering im Mittelstand: 5 Patterns, die wirklich funktionieren (aus 12 Mandaten)"
      subtitle={SUBTITLE}
      metaDescription="Fünf strukturelle Patterns aus zwölf Mandaten, die seit drei Jahren funktionieren. Mit Vorher-Nachher-Beispielen und dem häufigsten Fehler im Mittelstand."
      publishedDate="2026-05-26"
      readingTime="10 Min Lesezeit"
      category="KI in der Praxis"
    >
      <p className="text-lg">
        Es gibt einen ganzen Markt von „Prompt-Engineering"-Kursen, die
        Tricks verkaufen: Sag dem Modell, es sei ein Weltklasse-Experte. Biete
        ihm 200 Dollar Trinkgeld. Droh ihm mit Konsequenzen. Das funktioniert
        manchmal marginal, ist aber im Kern Aberglaube — Beobachtungen, die
        bei einem Modell mal halfen und bei der nächsten Version wirkungslos
        sind.
      </p>

      <p>
        Echtes Prompt-Engineering ist langweiliger und wirksamer. Es besteht
        aus einer Handvoll struktureller Patterns, die seit drei Jahren
        funktionieren und es weiter tun werden, weil sie nicht auf
        Modell-Quirks beruhen, sondern darauf, wie Sprachmodelle prinzipiell
        Kontext verarbeiten.
      </p>

      <p>
        Hier sind die fünf, die ich in zwölf Mandaten zwischen 2023 und 2026
        immer wieder eingesetzt habe — jeweils mit einem konkreten
        Vorher-Nachher aus der Praxis.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Pattern 1: Rolle + Kontext + Aufgabe + Format (das Grundgerüst)
      </h2>

      <p>
        Das wichtigste Pattern und das, gegen das am häufigsten verstoßen
        wird. Ein guter Prompt hat vier Komponenten in genau dieser
        Reihenfolge:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Rolle:</strong> Aus welcher fachlichen Perspektive soll
          geantwortet werden?
        </li>
        <li>
          <strong>Kontext:</strong> Welche Situation, welche
          Hintergrundinformation ist relevant?
        </li>
        <li>
          <strong>Aufgabe:</strong> Was genau soll getan werden?
        </li>
        <li>
          <strong>Format:</strong> Wie soll das Ergebnis strukturiert sein?
        </li>
      </ul>

      <p>
        <strong>Vorher</strong> (typischer Mittelstands-Prompt):{" "}
        <em>„Schreib mir eine E-Mail an einen Kunden, der nicht bezahlt
        hat."</em>
      </p>

      <p>
        <strong>Nachher:</strong> <em>„Du bist Forderungsmanager in einem
        B2B-Maschinenbau-Unternehmen. Kontext: Stammkunde seit 8 Jahren,
        Rechnung über 14.000 € ist 21 Tage überfällig, erste freundliche
        Erinnerung, Beziehung soll erhalten bleiben. Aufgabe: Schreibe eine
        Zahlungserinnerung. Format: max. 120 Wörter, höflich aber klar,
        konkretes neues Zahlungsziel, keine Drohung."</em>
      </p>

      <p>
        Der Unterschied im Output ist nicht graduell, er ist kategorial. Der
        erste Prompt produziert Durchschnitt. Der zweite produziert etwas, das
        Sie fast unverändert versenden können. Die Investition: 30 Sekunden
        mehr Denken.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Pattern 2: Few-Shot — Beispiele schlagen Beschreibungen
      </h2>

      <p>
        Wenn Sie wollen, dass ein Modell einen bestimmten Stil, ein bestimmtes
        Format oder eine bestimmte Logik trifft, ist das wirksamste Mittel
        nicht, es zu beschreiben — sondern es zu zeigen. Geben Sie zwei bis
        drei Beispiele des gewünschten Outputs, und das Modell extrahiert das
        Muster zuverlässiger, als jede Beschreibung es treffen könnte.
      </p>

      <p>
        <strong>Aus einem Mandat 2024:</strong> Ein Vertriebsteam wollte
        LinkedIn-Nachrichten im eigenen Ton generieren. Die Beschreibung
        „locker, aber professionell, nicht anbiedernd" führte zu generischem
        Output. Die Lösung: drei echte, erfolgreiche Nachrichten als Beispiele
        in den Prompt. Ab da traf das Modell den Ton fast jedes Mal. Kein
        Trick — nur Mustererkennung, richtig gefüttert.
      </p>

      <p>
        Faustregel: Wann immer Sie sich beim Beschreiben eines gewünschten
        Outputs schwertun, hören Sie auf zu beschreiben und fangen Sie an, zu
        zeigen.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Pattern 3: Denkschritte erzwingen (Chain-of-Thought)
      </h2>

      <p>
        Bei Aufgaben, die mehrstufiges Schließen erfordern — Analysen,
        Berechnungen, Bewertungen mit mehreren Kriterien — produzieren Modelle
        bessere Ergebnisse, wenn man sie zwingt, den Denkweg offenzulegen,
        bevor sie zum Ergebnis kommen.
      </p>

      <p>
        Der einfachste Hebel: ein Zusatz wie <em>„Gehe Schritt für Schritt
        vor: Liste zuerst die relevanten Faktoren auf, bewerte jeden einzeln,
        und ziehe dann erst das Fazit."</em> Das verhindert den häufigsten
        Fehler — dass das Modell vorschnell zu einer plausibel klingenden, aber
        unausgegorenen Antwort springt.
      </p>

      <p>
        <strong>Wichtige Einschränkung:</strong> Moderne Reasoning-Modelle
        (die o-Serie von OpenAI, bestimmte Claude- und Gemini-Modi) machen das
        intern bereits. Bei denen ist explizites Chain-of-Thought-Prompting
        teils redundant. Bei Standard-Modellen bleibt es einer der stärksten
        Qualitäts-Hebel.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Pattern 4: Negativ-Prompting — sagen, was NICHT sein soll
      </h2>

      <p>
        Modelle haben Standard-Tendenzen, die im Geschäftskontext oft stören:
        der typische „KI-Sound" (gestelzt, mit Phrasen wie „in der heutigen
        schnelllebigen Welt"), übertriebene Höflichkeit, ungefragte
        Zusammenfassungen, Listen wo Fließtext besser wäre.
      </p>

      <p>
        Diese Tendenzen verschwinden, wenn man sie explizit verbietet.{" "}
        <em>„Keine Einleitungsfloskeln. Kein Fazit-Absatz. Keine Aufzählung,
        sondern Fließtext. Vermeide die Wörter ‚innovativ', ‚nahtlos',
        ‚ganzheitlich'."</em>
      </p>

      <p>
        <strong>Aus der Praxis:</strong> Genau dieses Pattern nutze ich auch
        für die Texte, die mein eigenes System schreibt. Ohne Negativ-Prompts
        klingt jeder KI-Text nach KI. Mit einer guten Negativ-Liste klingt er
        nach Ihnen. Der Aufwand: einmal eine Verbots-Liste erstellen, dann in
        jedem Prompt wiederverwenden.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Pattern 5: System-Prompts + Templating statt Einmal-Prompts
      </h2>

      <p>
        Der Sprung von „gelegentlich ChatGPT nutzen" zu „KI produktiv im
        Unternehmen einsetzen" passiert genau hier: Sie hören auf, jedes Mal
        neu zu formulieren, und bauen wiederverwendbare Templates.
      </p>

      <p>
        Ein <strong>System-Prompt</strong> definiert einmal die Rolle, den
        Ton, die Verbote, die Format-Regeln — und gilt dann für alle folgenden
        Anfragen. Die eigentliche Aufgabe wird zur Variablen. Das ist der
        Unterschied zwischen Handarbeit und Prozess.
      </p>

      <p>
        <strong>Aus einem Mandat 2025:</strong> Ein Kundenservice-Team hatte
        zwölf wiederkehrende Anfrage-Typen. Statt für jede Antwort neu zu
        promoten, haben wir zwölf Templates gebaut — jeweils mit fixem
        System-Prompt und einer Lücke für den konkreten Kundenfall. Die
        Bearbeitungszeit pro Anfrage sank um zwei Drittel, und die Qualität
        wurde konsistenter, weil nicht mehr jeder Mitarbeiter anders promotete.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Der häufigste Fehler im Mittelstand
      </h2>

      <p>
        Es ist nicht der schlechte Prompt. Es ist die fehlende{" "}
        <strong>Prompt-Bibliothek</strong>. In den meisten Unternehmen, die KI
        „nutzen", promptet jeder Mitarbeiter für sich, niemand teilt, was
        funktioniert, und dasselbe Rad wird hundertfach neu erfunden — meist
        schlecht.
      </p>

      <p>
        Der Hebel mit dem besten Verhältnis von Aufwand zu Wirkung ist nicht
        das nächste Modell-Upgrade. Es ist eine geteilte, gepflegte Sammlung
        von bewährten Prompts und Templates für die wiederkehrenden Aufgaben
        Ihres Unternehmens. Das kostet ein paar Tage Aufbau und multipliziert
        die Wirkung jeder KI-Lizenz, die Sie ohnehin schon bezahlen.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Was sich nicht lohnt
      </h2>

      <p>
        Zur Ehrlichkeit gehört auch, was Sie ignorieren können:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>„Magische" Formulierungen</strong> — Trinkgeld-Versprechen,
          Drohungen, „Du bist der beste Experte der Welt". Marginal bis
          wirkungslos, und bei jeder Modell-Version anders.
        </li>
        <li>
          <strong>Extrem lange Prompts</strong> aus Angst, etwas zu vergessen.
          Klarheit schlägt Länge. Ein präziser 5-Zeilen-Prompt schlägt einen
          wirren 50-Zeilen-Prompt.
        </li>
        <li>
          <strong>Prompt-Marktplätze</strong>, auf denen Sie „1000 erprobte
          Prompts" kaufen. Ihre Aufgaben sind spezifisch — generische Prompts
          sind selten besser als die fünf Patterns oben, selbst angewendet.
        </li>
      </ul>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die praktische Quintessenz
      </h2>

      <p>
        Prompt-Engineering ist keine Geheimwissenschaft und kein Hexenwerk.
        Es ist die Disziplin, präzise zu sagen, was man will — eine Fähigkeit,
        die in jeder Führungsaufgabe nützlich ist, nicht nur im Umgang mit KI.
        Die fünf Patterns oben decken geschätzt 90 % dessen ab, was Sie im
        Geschäftsalltag brauchen.
      </p>

      <p>
        Mein konkreter Rat für jeden Mittelständler, der KI ernst meint:
        Investieren Sie zwei Tage, um mit einem kleinen Team Ihre zehn
        häufigsten KI-Aufgaben zu identifizieren und für jede ein sauberes
        Template nach diesen Patterns zu bauen. Das ist die günstigste
        KI-Investition mit dem höchsten Hebel, die Sie 2026 machen können —
        deutlich wirksamer als die nächste Lizenz-Aufstockung oder das nächste
        teurere Modell.
      </p>

      <p>
        Wer das einmal verstanden hat, hört auf, KI als Glücksspiel zu
        behandeln („mal sehen, was rauskommt"), und fängt an, sie als das zu
        nutzen, was sie ist: ein präzises Werkzeug, das so gut ist wie die
        Anweisung, die man ihm gibt.
      </p>
      <FoerderHinweis
        text="Auch Weiterbildung und externe Begleitung beim KI-Einstieg werden vielerorts bezuschusst — von Schulungen bis zur Prozessberatung. Ein Blick in die Programme des eigenen Bundeslands lohnt, bevor der erste Workshop gebucht wird."
        links={[
          { href: "https://foerdermittel.daaniyalkhan.com/programme/digitalisierung/bayern/", label: "Digitalisierungsförderung Bayern" },
          { href: "https://foerdermittel.daaniyalkhan.com/wissen/foerderantrag-fehler/", label: "Guide: Die häufigsten Antragsfehler" },
          { href: "https://foerdermittel.daaniyalkhan.com/", label: "Kostenloser Förder-Check" },
        ]}
      />

    </InsightLayout>
  );
};

export default PromptEngineeringMittelstand;
